import { Link, useParams, Navigate } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import JsonLd from "@/components/JsonLd";
import { getPost, POSTS } from "@/data/blog";
import { ArrowLeft, ArrowRight, Clock, Check } from "lucide-react";

function Block({ block }) {
  if (block.type === "h") return <h2 className="font-round text-2xl sm:text-3xl font-semibold mt-10 mb-3">{block.text}</h2>;
  if (block.type === "p") return <p className="text-lg text-sq-ink/80 leading-relaxed mb-4">{block.text}</p>;
  if (block.type === "ul") return (
    <ul className="space-y-2 mb-4">
      {block.items.map((it, i) => (
        <li key={i} className="flex gap-3 text-lg text-sq-ink/80"><Check className="text-sq-red shrink-0 mt-1.5" size={18} /> {it}</li>
      ))}
    </ul>
  );
  if (block.type === "ol") return (
    <ol className="space-y-3 mb-4 counter-reset">
      {block.items.map((it, i) => (
        <li key={i} className="flex gap-4 text-lg text-sq-ink/80">
          <span className="grid place-items-center w-8 h-8 rounded-full bg-sq-red text-white font-display text-lg shrink-0">{i + 1}</span>
          <span className="pt-0.5">{it}</span>
        </li>
      ))}
    </ol>
  );
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useSEO({
    title: post ? post.title : "Article",
    description: post ? post.excerpt : "",
    image: post ? post.image : undefined,
  });

  if (!post) return <Navigate to="/blog" replace />;

  const base = typeof window !== "undefined" ? window.location.origin : "";
  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <JsonLd id={`article-${post.slug}`} data={{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        image: post.image,
        datePublished: post.date,
        keywords: post.keywords,
        author: { "@type": "Person", name: post.author },
        publisher: { "@type": "Organization", name: "The Safety Quest" },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${base}/blog/${post.slug}` },
      }} />

      <article className="pt-36 pb-24 max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm font-semibold text-sq-ink/60 hover:text-sq-red mb-6">
            <ArrowLeft size={16} /> All resources
          </Link>
          <div className="flex items-center gap-3 text-sm text-sq-ink/50 font-semibold mb-4">
            <span>{new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            <span className="flex items-center gap-1"><Clock size={13} /> {post.readTime}</span>
            <span>· By {post.author}</span>
          </div>
          <h1 data-testid="post-title" className="font-display text-5xl sm:text-6xl tracking-tight leading-[0.9]">{post.title}</h1>
        </Reveal>

        <Reveal delay={0.1}>
          <img src={post.image} alt={post.title} className="w-full aspect-[16/9] object-cover rounded-[1.5rem] border-2 border-sq-ink mt-8" />
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10">
            {post.content.map((block, i) => <Block key={i} block={block} />)}
          </div>
        </Reveal>

        <div className="mt-12 rounded-3xl bg-sq-ink text-white p-8 text-center">
          <h3 className="font-round text-2xl font-semibold">Turn safety into an adventure</h3>
          <p className="text-white/70 mt-2">Meet Bobby & Pluggy and grab the book that makes these lessons stick.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-5">
            <Link to="/books" className="px-6 py-3 rounded-full bg-sq-yellow text-sq-ink font-bold btn-chunky">Explore the Books</Link>
            <Link to="/contact" className="px-6 py-3 rounded-full bg-white/10 text-white font-bold border border-white/20 hover:bg-white/15 transition-colors">Book a Program</Link>
          </div>
        </div>
      </article>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <h2 className="font-display text-4xl tracking-tight mb-8">Keep reading</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {related.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl bg-white border-2 border-sq-ink overflow-hidden hover:-translate-y-1 transition-transform">
              <img src={p.image} alt={p.title} loading="lazy" className="w-full aspect-[16/10] object-cover" />
              <div className="p-5">
                <h3 className="font-round text-lg font-semibold leading-tight">{p.title}</h3>
                <span className="inline-flex items-center gap-2 mt-3 text-sq-red font-bold text-sm group-hover:gap-3 transition-all">Read <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
